// AGC: FarmProHub Top Navigation Component
// AGC: Displays profile image, settings, network status, and token balance

import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { useAccount, useChainId, useSwitchChain, useBalance, useReadContract } from 'wagmi'
import { formatEther } from 'viem'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { SettingsModal } from './modals/SettingsModal'

interface TopNavProps {
  onOpenStaking?: () => void
  onOpenWalletFunds?: () => void
}

// AGC: Token contract ABI for reading balance
const TOKEN_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

// AGC: Token addresses from env
const FARM_PRO_TOKEN_ADDRESS = import.meta.env.VITE_OGB_TOKEN_ADDRESS as `0x${string}` | undefined
const USDC_ADDRESS = import.meta.env.VITE_USDC_ADDRESS as `0x${string}` | undefined

/**
 * AGC: FarmProHub top navigation bar.
 * AGC: Displays profile image (staking), settings, network status, and FarmPro token balance.
 */
export const TopNav: React.FC<TopNavProps> = ({
  onOpenStaking,
  onOpenWalletFunds,
}) => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { switchChain, isPending: isSwitchingChain } = useSwitchChain()
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const networkDropdownRef = useRef<HTMLDivElement>(null)

  // AGC: Get ETH balance
  const { data: ethBalance } = useBalance({
    address,
    query: {
      enabled: !!address,
    },
  })

  // AGC: Get FarmPro token balance
  const { data: farmProBalance } = useReadContract({
    address: FARM_PRO_TOKEN_ADDRESS,
    abi: TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!FARM_PRO_TOKEN_ADDRESS && !!address,
    },
  })

  // AGC: Get USDC balance
  const { data: usdcBalance } = useReadContract({
    address: USDC_ADDRESS,
    abi: TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!USDC_ADDRESS && !!address,
    },
  })

  // AGC: Close network dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (networkDropdownRef.current && !networkDropdownRef.current.contains(event.target as Node)) {
        setShowNetworkDropdown(false)
      }
    }

    if (showNetworkDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showNetworkDropdown])

  // AGC: Get current network name
  const getCurrentNetworkName = () => {
    if (chainId === 84532) return 'Base Sepolia'
    if (chainId === 8453) return 'Base'
    return `Chain ${chainId}`
  }

  // AGC: Handle network switch
  const handleSwitchNetwork = async (targetChainId: number) => {
    if (chainId === targetChainId) {
      setShowNetworkDropdown(false)
      return
    }

    try {
      await switchChain({ chainId: targetChainId })
      setShowNetworkDropdown(false)
    } catch (error) {
      console.error('Failed to switch network:', error)
      setShowNetworkDropdown(false)
    }
  }

  // AGC: Format token balance for display
  const formatTokenBalance = (balance: bigint | undefined, decimals: number = 18) => {
    if (!balance) return '0.00'
    const formatted = formatEther(balance)
    const num = parseFloat(formatted)
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`
    return num.toFixed(2)
  }

  // AGC: Format FarmPro balance
  const farmProBalanceDisplay = formatTokenBalance(farmProBalance)

  return (
    <>
      <nav className="fixed top-0 left-0 w-full h-[60px] px-4 flex items-center justify-between bg-slate-900/80 backdrop-blur-md border-b border-slate-800/70 z-50">
        {/* AGC: Left - Profile Image and Settings */}
        <div className="flex items-center gap-3">
          {/* AGC: Profile Image - Opens Staking */}
          {onOpenStaking && (
            <button
              onClick={onOpenStaking}
              className="p-0 hover:opacity-80 transition-opacity"
              aria-label="Open Staking"
              title="Staking"
            >
              <img 
                src="/profile1.png" 
                alt="Profile" 
                className="w-8 h-8 rounded-full object-cover"
              />
            </button>
          )}
          
          {/* AGC: Settings Icon */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 text-slate-400 hover:text-white transition-colors"
            aria-label="Open Settings"
          >
            <img 
              src="/setting.Png" 
              alt="Settings" 
              className="w-5 h-5 object-contain"
            />
          </button>
        </div>

        {/* AGC: Middle - Network and Status */}
        <div className="flex flex-col items-center leading-none relative">
          <div className="relative" ref={networkDropdownRef}>
            <button
              onClick={() => setShowNetworkDropdown(!showNetworkDropdown)}
              disabled={isSwitchingChain}
              className="flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white transition-colors disabled:opacity-50"
              aria-label="Switch network"
            >
              {isSwitchingChain ? 'Switching...' : getCurrentNetworkName()}
              <ChevronDown className={`w-3 h-3 transition-transform ${showNetworkDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {/* AGC: Network dropdown menu */}
            {showNetworkDropdown && (
              <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-lg min-w-[160px] overflow-hidden">
                <button
                  onClick={() => handleSwitchNetwork(84532)}
                  className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                    chainId === 84532
                      ? 'bg-[#0072CE] text-white'
                      : 'text-white/90 hover:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>Base Sepolia</span>
                    {chainId === 84532 && <span className="text-xs">✓</span>}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">Testnet</div>
                </button>
                <button
                  onClick={() => handleSwitchNetwork(8453)}
                  className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                    chainId === 8453
                      ? 'bg-[#0072CE] text-white'
                      : 'text-white/90 hover:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>Base</span>
                    {chainId === 8453 && <span className="text-xs">✓</span>}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">Mainnet</div>
                </button>
              </div>
            )}
          </div>
          <span className="text-[10px] text-[#0072CE]">online</span>
        </div>

        {/* AGC: Right - Token Balance or Connect Wallet */}
        <div className="flex items-center">
          {isConnected && address ? (
            <button
              onClick={onOpenWalletFunds}
              className="flex items-center gap-1 rounded-lg bg-[#0072CE]/80 px-3 py-1.5 text-xs font-semibold shadow-lg shadow-[#0072CE]/30 border border-[#0072CE]/30 hover:bg-[#0072CE] transition-colors"
              aria-label="View Wallet Funds"
            >
              <span className="text-white font-bold">FP</span>
              {farmProBalanceDisplay}
            </button>
          ) : (
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <button
                  onClick={openConnectModal}
                  className="px-4 py-1.5 bg-[#0072CE] hover:bg-[#005a9e] text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  Connect Wallet
                </button>
              )}
            </ConnectButton.Custom>
          )}
        </div>
      </nav>

      {/* AGC: Settings Modal */}
      <SettingsModal 
        isOpen={showSettingsModal} 
        onClose={() => setShowSettingsModal(false)} 
      />
    </>
  )
}
